import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import { toast } from 'react-toastify'
import StreamingManagementService from '../../services/StreamingManagementService'
import { GcpInput } from '../types'

function shortName(fullName: string) {
  const parts = fullName.split('/')
  return parts[parts.length - 1] || fullName
}

function formatDate(iso: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR')
}

export default function GcpInputsList() {
  const [inputs, setInputs] = useState<GcpInput[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<GcpInput | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await StreamingManagementService.getGcpInputs()
      setInputs(res?.data?.inputs ?? [])
    } catch {
      setError('Não foi possível carregar os inputs GCP.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await StreamingManagementService.deleteGcpInput(shortName(deleteTarget.name))
      toast.success(`Input "${shortName(deleteTarget.name)}" removido.`)
      setDeleteTarget(null)
      fetchData()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        'Erro ao remover o input. Verifique se não há channel associado.'
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Inputs GCP</Typography>
        <Tooltip title="Atualizar">
          <span>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>URI</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inputs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum input encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                inputs.map((input) => (
                  <TableRow key={input.name} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {shortName(input.name)}
                    </TableCell>
                    <TableCell>{input.type || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={input.uri || ''}>
                        <span>{input.uri || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{input.tier || '—'}</TableCell>
                    <TableCell>{formatDate(input.createTime)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remover input">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(input)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirm Delete Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Input</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover o input{' '}
            <strong>{deleteTarget ? shortName(deleteTarget.name) : ''}</strong>?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            A remoção falhará se este input estiver associado a um channel ativo.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
